(() => {
  if (window.customElements.get('collection-infinite-scroll')) return;

  const extractSectionId = (element) => {
    const section = element.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  };

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.nextLink = this.querySelector('a[rel="next"]');
      this.status = this.querySelector('.collection-infinite-scroll__status');
      this.loading = false;
      this.isIntersecting = false;
      this.abortController = null;

      if (!this.nextLink) return;

      this.observer = new IntersectionObserver((entries) => {
        this.isIntersecting = entries.some((entry) => entry.isIntersecting);
        if (this.isIntersecting) this.loadNext();
      }, {
        rootMargin: '0px 0px 800px 0px',
      });

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();
    }

    setBusy(isBusy) {
      this.loading = isBusy;
      this.status?.toggleAttribute('hidden', !isBusy);
      this.closest('.collection')?.querySelector('product-list')?.toggleAttribute('aria-busy', isBusy);
    }

    async loadNext() {
      if (this.loading || !this.nextLink || !this.isConnected) return;

      const sectionId = extractSectionId(this);
      const productList = this.closest('.collection')?.querySelector('product-list');
      if (!sectionId || !productList) return;

      const url = new URL(this.nextLink.href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      url.hash = '';

      this.abortController = new AbortController();
      this.setBusy(true);

      let loaded = false;

      try {
        const response = await fetch(url.toString(), { signal: this.abortController.signal });
        if (!response.ok) throw new Error('Failed to load next page');

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const remoteList = doc.querySelector('.collection product-list, product-list');
        if (!remoteList || !this.isConnected) return;

        const nodes = Array.from(remoteList.children).map((node) => document.importNode(node, true));
        nodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          node.removeAttribute('reveal-on-scroll');
          node.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
        });
        productList.append(...nodes);
        loaded = true;

        const remoteNext = doc.querySelector('collection-infinite-scroll a[rel="next"]');
        if (remoteNext) {
          this.nextLink.href = remoteNext.getAttribute('href');
        } else {
          this.setBusy(false);
          this.observer?.disconnect();
          this.nextLink = null;
          this.remove();
          return;
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
      } finally {
        if (this.isConnected) this.setBusy(false);
      }

      if (loaded && this.isConnected && this.nextLink && this.isIntersecting) {
        this.loadNext();
      }
    }
  }

  window.customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
})();
